import { useCallback, useMemo, useState } from "react";

// Format local YYYY-MM-DD (pas de toISOString => évite décalage UTC)
export const formatLocalDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getMonday = (date: Date) => {
  const day = date.getDay(); // 0=dim ... 6=sam
  const offset = day === 0 ? -6 : 1 - day;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + offset, 0, 0, 0, 0);
};

export interface UseWeekAnchorOptions {
  initialDate?: Date;
}

export function useWeekAnchor(opts: UseWeekAnchorOptions = {}) {
  const initial = opts.initialDate ? getMonday(opts.initialDate) : getMonday(new Date());
  const [anchorDate, setAnchorDate] = useState<Date>(initial);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate() + i, 0, 0, 0, 0));
  }, [anchorDate]);

  const isCurrentWeek = useMemo(() => {
    const currentMonday = getMonday(new Date());
    return formatLocalDate(currentMonday) === formatLocalDate(anchorDate);
  }, [anchorDate]);

  const goToNextWeek = useCallback(() => setAnchorDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7, 0, 0, 0, 0)), []);
  const goToPreviousWeek = useCallback(() => setAnchorDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - 7, 0, 0, 0, 0)), []);
  const goToToday = useCallback(() => setAnchorDate(getMonday(new Date())), []);
  const setAnchor = useCallback((date: Date) => setAnchorDate(getMonday(date)), []);

  const getDateISO = useCallback((index: number) => formatLocalDate(weekDays[index]), [weekDays]);

  return {
    anchorDate,
    weekDays,
    isCurrentWeek,
    goToNextWeek,
    goToPreviousWeek,
    goToToday,
    setAnchorDate: setAnchor,
    formatLocalDate,
    getDateISO,
  };
}
